import { StyleSheet, View, useWindowDimensions } from 'react-native'
import { useNavigation, useRoute } from '@react-navigation/native'
import type { NativeStackNavigationProp } from '@react-navigation/native-stack'
import type { RouteProp } from '@react-navigation/native'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import Animated, {
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'
import { PAIRING_CODE_LENGTH } from '@chillout/core'
import { supabase } from '../../../lib/supabase'
import { reportUnexpectedError } from '../../../lib/reportError'
import { ScreenContainer } from '../../shared/ScreenContainer'
import { CashierBackHeader } from '../shared/CashierBackHeader'
import { PairingScanQRContent } from './PairingScanQRContent'
import { PairingCodeContent } from './PairingCodeContent'
import type { CashierStackParamList } from '../../../navigation/types'

type Nav = NativeStackNavigationProp<CashierStackParamList, 'PairingInput'>
type Route = RouteProp<CashierStackParamList, 'PairingInput'>

const FLIP_DURATION = 500
const CARD_MAX_WIDTH = 480
const SCREEN_HORIZONTAL_PADDING = 48 // px-6 on each side

function getPairingErrorMessage(t: ReturnType<typeof useTranslation>['t'], code?: unknown) {
  switch (code) {
    case 'invalid_format':
      return t('cashierPairing.codeFormatError')
    case 'invalid_pairing_code':
      return t('cashierPairing.codeInvalidError')
    case 'pairing_code_expired':
      return t('cashierPairing.codeExpiredError')
    case 'internal_server_error':
      return t('cashierPairing.codeServerError')
    default:
      return t('cashierPairing.codeNetworkError')
  }
}

export function PairingInputScreen() {
  const { t } = useTranslation()
  const navigation = useNavigation<Nav>()
  const { params } = useRoute<Route>()
  const { width } = useWindowDimensions()
  const viewfinderSize = Math.min(width - SCREEN_HORIZONTAL_PADDING, CARD_MAX_WIDTH)

  const [mode, setMode] = useState<'scan' | 'code'>(params.initialMode)
  const [digits, setDigits] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const flipProgress = useSharedValue(params.initialMode === 'code' ? 1 : 0)

  const flip = (toMode: 'scan' | 'code') => {
    setMode(toMode)
    setError(null)
    flipProgress.value = withTiming(toMode === 'code' ? 1 : 0, {
      duration: FLIP_DURATION,
      easing: Easing.inOut(Easing.ease),
    })
  }

  const frontStyle = useAnimatedStyle(() => ({
    transform: [
      { perspective: 1200 },
      { rotateY: `${interpolate(flipProgress.value, [0, 1], [0, 180])}deg` },
    ],
    opacity: interpolate(flipProgress.value, [0, 0.45, 0.55, 1], [1, 1, 0, 0]),
  }))

  const backStyle = useAnimatedStyle(() => ({
    transform: [
      { perspective: 1200 },
      { rotateY: `${interpolate(flipProgress.value, [0, 1], [-180, 0])}deg` },
    ],
    opacity: interpolate(flipProgress.value, [0, 0.45, 0.55, 1], [0, 0, 1, 1]),
  }))

  const title = mode === 'scan'
    ? t('cashierPairing.scanQRScreenTitle')
    : t('cashierPairing.codeScreenTitle')

  const pairWithCode = async (code: string) => {
    if (isLoading) return

    setIsLoading(true)
    setError(null)
    try {
      const { data, error } = await supabase.functions.invoke('pair-device', {
        body: { code },
      })
      if (error || !data?.session) {
        // invalid_format/invalid_pairing_code/pairing_code_expired are routine
        // rejections a cashier can just fix and retry — internal_server_error
        // (or anything else unrecognized, or the invoke() call itself failing)
        // means something actually went wrong and is worth reporting.
        const expectedCodes = ['invalid_format', 'invalid_pairing_code', 'pairing_code_expired']
        if (!expectedCodes.includes(data?.error)) {
          reportUnexpectedError(error ?? new Error(data?.error ?? 'pair_device_failed'))
        }
        setError(getPairingErrorMessage(t, data?.error))
        return
      }

      try {
        await supabase.auth.setSession(data.session)
      } catch (sessionErr) {
        // The Edge Function already succeeded server-side — this is a purely
        // local SecureStore write failing, not a pairing rejection. Report it
        // since it's otherwise invisible (Supabase logs show a successful call).
        reportUnexpectedError(sessionErr)
        setError(t('cashierPairing.codeNetworkError'))
        return
      }

      navigation.navigate('PairingSuccess', {
        deviceName: data.device_name,
        branchName: data.branch_name,
      })
    } catch {
      setError(t('cashierPairing.codeNetworkError'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <ScreenContainer>
      <CashierBackHeader title={title} />
      <View className="flex-1 items-center px-6 pb-10">
        <View className="w-full max-w-[480px] flex-1">

          <Animated.View
            style={[StyleSheet.absoluteFillObject, frontStyle]}
            pointerEvents={mode === 'scan' ? 'auto' : 'none'}
          >
            <PairingScanQRContent
              viewfinderSize={viewfinderSize}
              isScanning={isLoading}
              error={mode === 'scan' ? error : null}
              onScanned={(code) => {
                setDigits(code.split(''))
                void pairWithCode(code)
              }}
              onInvalidScan={() => setError(t('cashierPairing.scanInvalidError'))}
              onSwitchToCode={() => flip('code')}
            />
          </Animated.View>

          <Animated.View
            style={[StyleSheet.absoluteFillObject, backStyle]}
            pointerEvents={mode === 'code' ? 'auto' : 'none'}
          >
            <PairingCodeContent
              digits={digits}
              onDigit={(d) => {
                if (digits.length < PAIRING_CODE_LENGTH) {
                  setDigits((prev) => [...prev, d])
                  setError(null)
                }
              }}
              onDelete={() => {
                setDigits((prev) => prev.slice(0, -1))
                setError(null)
              }}
              canConfirm={digits.length === PAIRING_CODE_LENGTH}
              isLoading={isLoading}
              error={error}
              onConfirm={() => void pairWithCode(digits.join(''))}
              onSwitchToScan={() => flip('scan')}
            />
          </Animated.View>

        </View>
      </View>
    </ScreenContainer>
  )
}
